import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../services/api';
import type { PaymentDetail } from '../services/types';
import { formatDate, formatMoney } from '../utils/format';
import { useAuth } from '../hooks/useAuth';

const STATUSES = ['pending', 'processed', 'failed', 'refunded'];

export function PaymentDetailPage() {
  const { paymentId } = useParams<{ paymentId: string }>();
  const { user } = useAuth();
  const canWrite = user?.role === 'finance' || user?.role === 'superadmin';

  const [payment, setPayment] = useState<PaymentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!paymentId) return;
    (async () => {
      setLoading(true);
      const res = await api.getPayment(decodeURIComponent(paymentId));
      if (res.data) {
        setPayment(res.data);
        setNewStatus(res.data.status);
      } else {
        setError(res.error?.message ?? 'Платёж не найден');
      }
      setLoading(false);
    })();
  }, [paymentId]);

  const updateStatus = async () => {
    if (!paymentId || !newStatus) return;
    setBusy(true);
    const res = await api.updatePaymentStatus(decodeURIComponent(paymentId), newStatus);
    if (res.data) {
      setPayment(res.data);
    } else if (res.error) {
      setError(res.error.message);
    }
    setBusy(false);
  };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>;
  if (error && !payment) return <p style={{ color: 'var(--danger)' }}>{error}</p>;
  if (!payment) return null;

  return (
    <div>
      <p style={{ marginTop: 0 }}>
        <Link to="/payments">← Платежи</Link>
      </p>

      <h1 style={{ marginTop: 0 }}>Платёж</h1>

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <dl className="detail-grid">
          <dt>ID</dt>
          <dd>
            <code>{payment.id}</code>
          </dd>
          <dt>User</dt>
          <dd>
            <Link to={`/users/${payment.user_id}`}>
              {payment.first_name || payment.username || payment.user_id}
            </Link>
          </dd>
          <dt>Сумма</dt>
          <dd>{formatMoney(payment.amount)}</dd>
          <dt>Период</dt>
          <dd>{payment.period} мес · {payment.days} дн</dd>
          <dt>Tier</dt>
          <dd>{payment.subscription_tier ?? '—'}</dd>
          <dt>Статус</dt>
          <dd>{payment.status}</dd>
          <dt>Создан</dt>
          <dd>{formatDate(payment.created_at)}</dd>
          <dt>Payment method</dt>
          <dd>{payment.payment_method_id ?? '—'}</dd>
          <dt>Подписка user</dt>
          <dd>
            {formatDate(payment.user_subscription_until)}
            {payment.user_subscription_tier ? ` · ${payment.user_subscription_tier}` : ''}
          </dd>
        </dl>
      </div>

      {canWrite && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Изменить статус</h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select className="select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={updateStatus}>
              Сохранить
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
